import { useEffect, useRef, useState } from 'react';
import type { DemoKey, DemoScript, FingerKey, Fx, RoundProps, ViewProps } from '../kit.js';
import { BAD, BoardFrame, INK, OK, TYPE, WARN, alpha, easier, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import type { Status } from '../kit.js';
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';

/**
 * ENGINEER · HOLD THE PRESSURE — hold to pump, let go to vent, keep the needle in the green.
 *
 * A big steam gauge and a hand pump. Holding anywhere on the board works the pump and the needle
 * climbs; letting go, the pressure leaks away and the needle falls. While the needle is in the
 * green band the bar under the gauge fills; fill it and the round is won. Past the green there is
 * a short amber margin, and past that the red: the needle in the red blows the relief valve, which
 * is the mistake — steam, the needle drops, the bar loses a share, and the pump waits for the
 * finger to lift and press again. The status line says what to do now: PUMP INTO THE GREEN, HOLD
 * IT IN THE GREEN, TOO HIGH — LET GO.
 *
 * Round 1 the band is wide and the needle does just what the finger says. Round 2 the band is a
 * little narrower, the needle has some weight (it runs on a little after you let go) and the leak
 * comes and goes. Round 3 narrower again, heavier, with a livelier leak and a longer bar to fill.
 *
 * Converted to the kit (kit.tsx): the rules as plain functions, a small view state
 * (`PressureView`), a pure `View`, the `Board` that plays it on the game clock, and the `demo`.
 */

export interface PressureLevel {
  /** How wide the green band is, as a share of the dial. */
  band: number;
  /** Where the band's middle may sit: the least and the most. */
  mid: readonly [number, number];
  /** How fast the needle climbs while you hold, in dial per second. */
  pump: number;
  /** How fast it falls when you let go. */
  leak: number;
  /** How much the leak swells and ebbs, ± this share of itself. */
  leakSwing: number;
  /** One swell and ebb of the leak, in seconds. */
  swingS: number;
  /** How quickly the needle takes up a new speed (per second): high is crisp, low has weight. */
  grip: number;
  /** The amber margin between the green and the red. */
  margin: number;
  /** Seconds in the green to fill the bar. */
  holdS: number;
  /** The share of the bar a blown valve costs. */
  blowLoss: number;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const PRESSURE_LEVELS: readonly PressureLevel[] = easier(
  [
    { band: 0.26, mid: [0.52, 0.56], pump: 0.32, leak: 0.22, leakSwing: 0, swingS: 4, grip: 12, margin: 0.1, holdS: 2.6, blowLoss: 0.3 },
    { band: 0.19, mid: [0.5, 0.6], pump: 0.35, leak: 0.24, leakSwing: 0.3, swingS: 3.4, grip: 6, margin: 0.08, holdS: 3, blowLoss: 0.3 },
  ],
);

/** How long the valve vents after it blows; the pump is idle meanwhile. */
export const VENT_MS = 900;

export interface Gauge {
  /** The needle, 0 (empty) to 1 (full scale). */
  p: number;
  /** Its speed, dial per second. */
  v: number;
}

/** The leak at game time `t` seconds: it swells and ebbs on the harder rounds. */
export const leakAt = (lv: PressureLevel, t: number) => lv.leak * (1 + lv.leakSwing * Math.sin((2 * Math.PI * t) / lv.swingS));

/** One step of the needle: it eases toward the pump's speed while held, the leak's when not. */
export function stepGauge(g: Gauge, held: boolean, dt: number, lv: PressureLevel, t: number): Gauge {
  const want = held ? lv.pump : -leakAt(lv, t);
  const v = g.v + (want - g.v) * Math.min(1, lv.grip * dt);
  const p = Math.min(1, Math.max(0, g.p + v * dt));
  return { p, v: p <= 0 && v < 0 ? 0 : v };
}

export type Zone = 'low' | 'green' | 'high' | 'red';

export interface Band {
  lo: number;
  hi: number;
  red: number;
}

/** The band for one round, its middle somewhere in the level's range. */
export function bandOf(lv: PressureLevel, rand: () => number): Band {
  const mid = lv.mid[0] + (lv.mid[1] - lv.mid[0]) * rand();
  const lo = mid - lv.band / 2;
  const hi = mid + lv.band / 2;
  return { lo, hi, red: hi + lv.margin };
}

export function zoneOf(p: number, band: Band): Zone {
  if (p >= band.red) return 'red';
  if (p > band.hi) return 'high';
  if (p >= band.lo) return 'green';
  return 'low';
}

/** While the valve vents, the needle drops fast to a little below the green, whatever the finger does. */
export const ventFloor = (band: Band) => Math.max(0.05, band.lo - 0.14);

// -- the view -------------------------------------------------------------------------------------

/** Everything the gauge shows. */
export interface PressureView {
  /** The game clock, ms. */
  t: number;
  /** The needle, 0–1 of the dial. */
  p: number;
  /** The green band's bottom and top, and where the red starts (0–1 of the dial). */
  lo: number;
  hi: number;
  red: number;
  /** The bar: time held in the green, 0–1. */
  bar: number;
  /** The share the last blow cost, while it fades from the bar: where the bar stood, and how long ago (ms). */
  lost: readonly [from: number, age: number] | null;
  /** Ms since the valve last blew, while its steam shows; null otherwise. */
  vent: number | null;
  /** The pump's handle, 0 up to 1 down. */
  hd: number;
  /** The pump is working. */
  pm: boolean;
  /** A finger still down after a blow: the pump waits for it to lift. */
  re: boolean;
  /** 0 playing, 1 the pressure held. */
  s: 0 | 1;
  fx?: Fx | undefined;
}

/** The rim of the gauge, by era: steel, chrome, gunmetal, brass, bronze. */
const BEZEL: Record<Era, readonly [string, string, string]> = {
  stars: ['#e3ebf3', '#6f7e8e', '#1f2731'],
  neon: ['#f7e6ff', '#8b6ea3', '#241832'],
  debris: ['#dccbc6', '#6f5853', '#251515'],
  dust: ['#ffe3aa', '#b27b36', '#382210'],
  canopy: ['#e6f0d2', '#7f9164', '#1e2917'],
};

const CX = 126;
const CY = 134;
/** The dial runs 240°, from the lower left round the top to the lower right. */
const angleOf = (p: number) => -120 + 240 * Math.min(1.02, Math.max(-0.02, p));
const at = (p: number, r: number): [number, number] => {
  const a = (angleOf(p) * Math.PI) / 180;
  return [CX + r * Math.sin(a), CY - r * Math.cos(a)];
};
const arc = (p1: number, p2: number, r: number) => {
  const [x1, y1] = at(p1, r);
  const [x2, y2] = at(p2, r);
  return `M${x1.toFixed(2)} ${y1.toFixed(2)} A${r} ${r} 0 ${(p2 - p1) * 240 > 180 ? 1 : 0} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
};
const TICKS = Array.from({ length: 41 }, (_, i) => i / 40);

const BAR_X = 16;
const BAR_W = 288;
const BAR_Y = 286;
const PUMP_X = 272;
const VALVE_X = 226;
const VALVE_TOP = 194;
/** A blow's red ghost fades from the bar over this long; its steam lasts this long. */
const LOST_MS = 900;
const STEAM_MS = 1_600;

/** The gauge and the pump, from their view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<PressureView>) {
  const skin = ERA_SKIN[era];
  const id = useSvgId();
  const bezel = BEZEL[era];
  const band = { lo: view.lo, hi: view.hi, red: view.red };
  const { p, t } = view;
  const zone = zoneOf(p, band);
  const done = Math.round(view.s) === 1;
  const venting = view.vent !== null && view.vent < VENT_MS;
  const pumping = view.pm && !venting && !done;
  // A faint tremble in the needle while the pump works: motion, not light.
  const needleDeg = angleOf(p) + (pumping ? Math.sin(t / 23) * 0.7 : 0);
  const status: Status = done
    ? { text: say(lang, '✓ PRESSURE HELD', '✓ TRYKKET HOLDT'), tone: 'ok' }
    : venting
      ? { text: say(lang, 'VALVE BLEW — AGAIN', 'VENTILEN GIKK – IGJEN'), tone: 'bad' }
      : view.re
        ? { text: say(lang, 'LET GO, THEN HOLD AGAIN', 'SLIPP, OG HOLD IGJEN'), tone: 'warn' }
        : zone === 'high'
          ? { text: say(lang, 'TOO HIGH — LET GO', 'FOR HØYT – SLIPP'), tone: 'warn' }
          : zone === 'green'
            ? { text: say(lang, 'HOLD IT IN THE GREEN', 'HOLD DEN I DET GRØNNE'), tone: 'ok' }
            : { text: say(lang, 'PUMP INTO THE GREEN', 'PUMP OPP I DET GRØNNE'), hint: say(lang, 'HOLD TO PUMP', 'HOLD FOR Å PUMPE') };

  // Steam from the valve: a burst when it blows, a thin hiss while the needle is in the amber.
  const puffs: { x: number; y: number; r: number; o: number }[] = [];
  if (view.vent !== null && view.vent < STEAM_MS) {
    const k0 = view.vent / 1_100;
    for (let i = 0; i < 4; i++) {
      const k = k0 - i * 0.12;
      if (k <= 0 || k >= 1) continue;
      const o = (k < 0.15 ? k / 0.15 : 1 - (k - 0.15) / 0.85) * 0.5;
      puffs.push({ x: VALVE_X + (i % 2 ? 7 : -6) * k * 2, y: VALVE_TOP - 6 - k * 60, r: 6 + k * 18, o });
    }
  } else if (zone === 'high' && !done) {
    const k = (t / 650) % 1;
    puffs.push({ x: VALVE_X + 2, y: VALVE_TOP - 4 - k * 22, r: 2.5 + k * 6, o: 0.3 * (1 - k) });
  }
  const lostK = view.lost ? view.lost[1] / LOST_MS : 1;

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}bz`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={bezel[0]} />
          <stop offset=".5" stopColor={bezel[1]} />
          <stop offset="1" stopColor={bezel[2]} />
        </linearGradient>
        <radialGradient id={`${id}face`} cx=".5" cy=".42" r=".6">
          <stop offset="0" stopColor="#1c2631" />
          <stop offset="1" stopColor="#070b10" />
        </radialGradient>
        <linearGradient id={`${id}steel`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#39424b" />
          <stop offset=".35" stopColor="#dfe7ee" />
          <stop offset="1" stopColor="#2c333a" />
        </linearGradient>
        <linearGradient id={`${id}pipe`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2a3139" />
          <stop offset=".4" stopColor="#b9c4ce" />
          <stop offset="1" stopColor="#20262d" />
        </linearGradient>
        <radialGradient id={`${id}hub`} cx=".35" cy=".35" r=".7">
          <stop offset="0" stopColor={INK.hi} />
          <stop offset=".5" stopColor={bezel[1]} />
          <stop offset="1" stopColor={bezel[2]} />
        </radialGradient>
        <linearGradient id={`${id}glass`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={INK.hi} stopOpacity=".13" />
          <stop offset=".5" stopColor={INK.hi} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* the pipe from the pump to the gauge, and the relief valve on it */}
      <rect x="186" y="210" width="80" height="14" fill={`url(#${id}pipe)`} />
      <rect x={VALVE_X - 5} y={VALVE_TOP + 4} width="10" height="14" fill={`url(#${id}steel)`} />
      <rect x={VALVE_X - 9} y={VALVE_TOP} width="18" height="6" rx="2" fill={`url(#${id}steel)`} />
      <rect x={VALVE_X - 11} y="206" width="22" height="22" rx="4" fill={`url(#${id}steel)`} />
      <circle cx={VALVE_X} cy="217" r="5" fill="#1b2128" />
      <circle cx={VALVE_X} cy="217" r="2" fill={zone === 'high' || venting ? WARN : '#3a444e'} />
      {puffs.map((f, i) => (
        <circle key={i} cx={f.x} cy={f.y} r={f.r} fill={INK.text} opacity={f.o} />
      ))}

      {/* the hand pump: a barrel, and a plunger that strokes while you hold */}
      <rect x={PUMP_X - 18} y="100" width="36" height="136" rx="6" fill={`url(#${id}steel)`} opacity=".9" />
      <rect x={PUMP_X - 14} y="108" width="4" height="120" rx="2" fill={INK.hi} opacity=".18" />
      <rect x={PUMP_X - 22} y="230" width="44" height="12" rx="3" fill={bezel[2]} stroke={bezel[1]} strokeWidth="1" />
      <g transform={`translate(0 ${view.hd * 26})`}>
        <rect x={PUMP_X - 4} y="50" width="8" height="54" fill={`url(#${id}steel)`} />
        <rect x={PUMP_X - 24} y="40" width="48" height="12" rx="6" fill={`url(#${id}hub)`} stroke={bezel[2]} strokeWidth="1" />
      </g>
      <rect x={PUMP_X - 22} y="96" width="44" height="8" rx="2" fill={bezel[1]} />
      <text x={PUMP_X} y="262" textAnchor="middle" fontSize={TYPE.hint} fontWeight="700" fontFamily={mono} letterSpacing="1.5" fill={pumping ? INK.hi : INK.dim}>
        {say(lang, 'PUMP', 'PUMPE')}
      </text>

      {/* the gauge: bezel, face, the coloured bands */}
      <circle cx={CX + 3} cy={CY + 5} r="108" fill="#000000" opacity=".4" />
      <circle cx={CX} cy={CY} r="108" fill={`url(#${id}bz)`} />
      <circle cx={CX} cy={CY} r="99" fill={bezel[2]} />
      <circle cx={CX} cy={CY} r="95" fill={`url(#${id}face)`} />
      <circle cx={CX} cy={CY} r="95" fill="none" stroke={skin.ink} strokeOpacity=".25" />

      <path d={arc(0, 1, 78)} stroke={INK.hi} strokeOpacity=".08" strokeWidth="13" fill="none" />
      <path d={arc(band.lo, band.hi, 78)} stroke={OK} strokeWidth="13" fill="none" opacity={zone === 'green' ? 1 : 0.75} />
      <path d={arc(band.hi, band.red, 78)} stroke={WARN} strokeWidth="13" fill="none" opacity=".85" />
      <path d={arc(band.red, 1, 78)} stroke={BAD} strokeWidth="13" fill="none" opacity=".9" />
      {TICKS.map((k, i) => {
        const major = i % 8 === 0;
        const [x1, y1] = at(k, major ? 86 : 88);
        const [x2, y2] = at(k, 93);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={INK.text} strokeOpacity={major ? 0.9 : 0.4} strokeWidth={major ? 2 : 1} />;
      })}
      {[0, 0.2, 0.4, 0.6, 0.8, 1].map((k, i) => {
        const [x, y] = at(k, 60);
        return (
          <text key={k} x={x} y={y + 4} textAnchor="middle" fontSize={TYPE.label} fontFamily={mono} fill={INK.text} opacity=".75">
            {i * 2}
          </text>
        );
      })}
      <text x={CX} y={CY + 44} textAnchor="middle" fontSize={TYPE.hint} letterSpacing="2" fontFamily={mono} fill={skin.ink} opacity=".85">
        {say(lang, 'PRESSURE', 'TRYKK')}
      </text>

      {/* the needle, with its shadow */}
      <g transform={`rotate(${needleDeg.toFixed(2)} ${CX + 2} ${CY + 3})`} opacity=".4">
        <path d={`M${CX + 2 - 4} ${CY + 3 + 20} L${CX + 2 + 4} ${CY + 3 + 20} L${CX + 2 + 1.3} ${CY + 3 - 82} L${CX + 2 - 1.3} ${CY + 3 - 82} Z`} fill="#000000" />
      </g>
      <g transform={`rotate(${needleDeg.toFixed(2)} ${CX} ${CY})`}>
        <path d={`M${CX - 4.2} ${CY + 20} L${CX + 4.2} ${CY + 20} L${CX + 1.3} ${CY - 82} L${CX - 1.3} ${CY - 82} Z`} fill={INK.hi} stroke="#0b1220" strokeOpacity=".7" />
        <path d={`M${CX + 0.4} ${CY + 14} L${CX + 0.6} ${CY - 78}`} stroke="#000000" strokeWidth="1.4" opacity=".15" />
      </g>
      <circle cx={CX} cy={CY} r="12" fill={`url(#${id}hub)`} />
      <circle cx={CX} cy={CY} r="4" fill={bezel[2]} />

      {/* the glass over the face */}
      <path d={`M${CX - 88} ${CY - 20} A92 92 0 0 1 ${CX + 30} ${CY - 90} A118 118 0 0 0 ${CX - 88} ${CY - 20} Z`} fill={`url(#${id}glass)`} />

      {/* the bar: time held in the green */}
      <rect x={BAR_X} y={BAR_Y} width={BAR_W} height="18" rx="9" fill="#05080c" stroke={INK.hi} strokeOpacity=".16" />
      {view.lost && lostK < 1 && (
        <rect x={BAR_X + 2} y={BAR_Y + 2} width={Math.max(0, (BAR_W - 4) * view.lost[0])} height="14" rx="7" fill={BAD} opacity={0.55 * (1 - lostK)} />
      )}
      <rect x={BAR_X + 2} y={BAR_Y + 2} width={Math.max(0, (BAR_W - 4) * view.bar)} height="14" rx="7" fill={OK} />
      <rect x={BAR_X + 2} y={BAR_Y + 2} width={Math.max(0, (BAR_W - 4) * view.bar)} height="5" rx="2.5" fill={INK.hi} opacity=".25" />
      {Array.from({ length: 9 }, (_, i) => (
        <line key={i} x1={BAR_X + ((i + 1) * BAR_W) / 10} y1={BAR_Y + 3} x2={BAR_X + ((i + 1) * BAR_W) / 10} y2={BAR_Y + 15} stroke="#000000" strokeOpacity=".35" />
      ))}
      {done && <rect x={BAR_X} y={BAR_Y} width={BAR_W} height="18" rx="9" fill="none" stroke={alpha(OK, 0.8)} strokeWidth="2" />}
    </BoardFrame>
  );
}

// -- the board -----------------------------------------------------------------------------------

/** The pump's handle strokes this many times a second while held. */
const STROKES = 1.7;

function Board(props: RoundProps) {
  const { level, lang } = props;
  const lv = PRESSURE_LEVELS[level]!;
  const [band] = useState(() => bandOf(lv, seeded(Date.now() + level * 7_331)));
  const gauge = useRef<Gauge>({ p: 0.04, v: 0 });
  const held = useRef(false);
  /** The finger is on the board: after a blow the pump waits for it to lift and press again. */
  const down = useRef(false);
  const bar = useRef(0);
  const lost = useRef<{ from: number; at: number } | null>(null);
  const ventAt = useRef<number | null>(null);
  const stroke = useRef(0);
  const handle = useRef(0);

  const round = useRound(props, (dt, now, won) => {
    const venting = ventAt.current !== null && now - ventAt.current < VENT_MS;
    const pumping = held.current && !venting && !won;
    if (venting) {
      gauge.current = { p: Math.max(ventFloor(band), gauge.current.p - 0.9 * dt), v: 0 };
    } else if (!won) {
      gauge.current = stepGauge(gauge.current, pumping, dt, lv, now / 1000);
    } else {
      // Won: the pressure settles to the middle of the green and stays.
      const mid = (band.lo + band.hi) / 2;
      gauge.current = { p: gauge.current.p + (mid - gauge.current.p) * Math.min(1, 3 * dt), v: 0 };
    }
    // The pump's handle strokes while held and comes back up when let go.
    if (pumping) {
      stroke.current += dt * STROKES;
      handle.current = (1 - Math.cos(2 * Math.PI * stroke.current)) / 2;
    } else {
      stroke.current = 0;
      handle.current = Math.max(0, handle.current - dt * 3);
    }
    if (won || venting) return;
    const zone = zoneOf(gauge.current.p, band);
    if (zone === 'red') {
      ventAt.current = now;
      held.current = false;
      lost.current = { from: bar.current, at: now };
      bar.current = Math.max(0, bar.current - lv.blowLoss);
      round.progress(Math.floor(bar.current * 20) / 20);
      round.miss();
      return;
    }
    if (zone === 'green') {
      bar.current = Math.min(1, bar.current + dt / lv.holdS);
      // The bar moves every frame; the station hears it in steps of a twentieth.
      round.progress(Math.floor(Math.min(0.95, bar.current) * 20) / 20);
      if (bar.current >= 1) {
        held.current = false;
        round.win();
      }
    }
  });

  // Covered mid-pump: the pump stops, as if let go.
  useEffect(() => {
    if (props.paused) held.current = false;
  }, [props.paused]);

  const press = () => {
    down.current = true;
    if (!round.live) return;
    if (ventAt.current !== null && round.time() - ventAt.current < VENT_MS) return;
    held.current = true;
  };
  const release = () => {
    down.current = false;
    held.current = false;
  };

  const now = round.now;
  const venting = ventAt.current !== null && now - ventAt.current < VENT_MS;
  const view: PressureView = {
    t: q(now),
    p: q(gauge.current.p, 3),
    lo: q(band.lo, 3),
    hi: q(band.hi, 3),
    red: q(band.red, 3),
    bar: q(bar.current, 3),
    lost: lost.current && now - lost.current.at < LOST_MS ? [q(lost.current.from, 3), q(now - lost.current.at)] : null,
    vent: ventAt.current !== null && now - ventAt.current < STEAM_MS ? q(now - ventAt.current) : null,
    hd: q(handle.current, 2),
    pm: held.current && !venting && !round.won,
    re: down.current && !held.current && !round.won && !venting && round.live,
    s: round.won ? 1 : 0,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Hold to pump', 'Hold for å pumpe'), down: press, up: release }}
    />
  );
}

// -- the how-to ----------------------------------------------------------------------------------

/**
 * The how-to, pumped by the rules themselves: round 1, a steady hand that holds until the needle
 * nears the top of the green and lets go until it nears the bottom, until the bar is full. Worked
 * out once and keyed every tenth of a second; the finger is down whenever the pump works.
 */
function demoPump(): DemoScript<PressureView> {
  const lv = PRESSURE_LEVELS[0]!;
  const band: Band = bandOf(lv, () => 0.5);
  const ms = 7_000;
  const start = 700;
  const finger: FingerKey[] = [
    { at: 0, x: 150, y: 300, hide: true },
    { at: 250, x: 150, y: 300, hide: false },
    { at: 550, x: 160, y: 250 },
  ];
  const keys: DemoKey<PressureView>[] = [];
  let g: Gauge = { p: 0.04, v: 0 };
  let bar = 0;
  let held = false;
  let since = -1e9;
  let stroke = 0;
  let handle = 0;
  let wonAt: number | null = null;
  const releases: number[] = [];
  const dt = 1 / 60;
  for (let t = 0, frame = 0; t <= ms; t += dt * 1000, frame++) {
    if (wonAt === null && t >= start) {
      // Hold up to near the top of the green, let go down to near its bottom; no hold or rest under a quarter second.
      const want: boolean = held ? g.p < band.hi - 0.04 : g.p < band.lo + 0.06;
      if (want !== held && t - since > 250) {
        held = want;
        since = t;
        finger.push({ at: Math.round(t), x: 160, y: 250, down: held });
        if (!held) releases.push(Math.round(t));
      }
    }
    if (wonAt === null) g = stepGauge(g, held, dt, lv, t / 1000);
    else g = { p: g.p + ((band.lo + band.hi) / 2 - g.p) * Math.min(1, 3 * dt), v: 0 };
    if (held) {
      stroke += dt * STROKES;
      handle = (1 - Math.cos(2 * Math.PI * stroke)) / 2;
    } else {
      stroke = 0;
      handle = Math.max(0, handle - dt * 3);
    }
    if (wonAt === null && zoneOf(g.p, band) === 'green') {
      bar = Math.min(1, bar + dt / lv.holdS);
      if (bar >= 1) {
        wonAt = t;
        if (held) {
          held = false;
          finger.push({ at: Math.round(t), x: 160, y: 250, down: false });
        }
      }
    }
    if (frame % 6 === 0) {
      keys.push({
        at: Math.round(t),
        view: {
          t: 0,
          p: q(g.p, 3),
          lo: q(band.lo, 3),
          hi: q(band.hi, 3),
          red: q(band.red, 3),
          bar: q(bar, 3),
          lost: null,
          vent: null,
          hd: q(handle, 2),
          pm: held,
          re: false,
          s: wonAt === null ? 0 : 1,
          fx: wonAt !== null && t - wonAt < 450 ? [q(1 - (t - wonAt) / 450, 2), 0] : [0, 0],
        },
      });
    }
  }
  finger.push({ at: ms - 700, x: 150, y: 300, hide: true });
  const firstHold = finger.find((f) => f.down === true)?.at ?? start;
  const firstRelease = releases[0] ?? 2_500;
  return {
    ms,
    keys,
    finger,
    steps: [
      { at: 0, en: 'HOLD TO PUMP', no: 'HOLD FOR Å PUMPE' },
      { at: Math.max(firstHold + 600, firstRelease - 300), en: 'LET GO AND IT FALLS', no: 'SLIPP, SÅ FALLER DEN' },
      { at: Math.max(firstRelease + 900, (releases[1] ?? firstRelease + 1_400) - 300), en: 'KEEP IT IN THE GREEN', no: 'HOLD DEN I DET GRØNNE' },
    ],
  };
}

export const game = thirtyGame<PressureView>({
  Board,
  View,
  demo: demoPump(),
  words: {
    en: {
      instruction: 'Hold to pump, let go to vent. Keep the needle in the green until the bar is full.',
      howMove: 'Hold your finger on the board to pump. Let go and the pressure falls.',
    },
    no: {
      instruction: 'Hold for å pumpe, slipp for å lette. Hold nålen i det grønne til søylen er full.',
      howMove: 'Hold fingeren på brettet for å pumpe. Slipp, så faller trykket.',
    },
  },
});
